/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import BaseSearchMapping from './BaseSearchMappings';
import SearchFieldDeviceAnyTooltip from './DeviceAnyFieldTooltip';
import SearchFieldDefaultTooltip from './SearchFieldDefaultTooltip';
import {copyPasteQueryNames} from './types';

/**
 * Defines the `SearchFieldTooltip` Prop types definitions, which requires the
 * name of the search field, a reference to the search instance, and an optional
 * limit.
 */
export interface SearchFieldTooltipProps {
  queryName: string;
  searchInstance: BaseSearchMapping;
  limit?: number;
}

/**
 * Provides an abstraction for determining if a custom Tooltip is required, and if
 * so, returns the custom implementation, otherwise, returns `undefined` so as to
 * signal that the `SearchFieldDefaultTooltip` is to be used by default.
 */
const getCustomToolTip = ({queryName, searchInstance}: SearchFieldTooltipProps, title: string) => {
  const isDeviceAnySearch = (
    searchInstance.getMappingObject(copyPasteQueryNames.ESN) &&
    queryName === copyPasteQueryNames.ANY
  );
  if (isDeviceAnySearch) {
    return <SearchFieldDeviceAnyTooltip title={title} />;
  }
};

/**
 * @internal
 *
 * The `SearchFieldTooltip` component determines the ToolTip implementation which is
 * to be displayed based on the selected Field. The `SearchFieldDefaultTooltip` will
 * be displayed for all fields which do not require an custom implementation, such as
 * the ANY field for Device Search.
 *
 * This component is used by the `ModularSearchWidget` internally and generally not
 * exposed  to clients.
 */
const SearchFieldTooltip = (params: SearchFieldTooltipProps) => {
  const title = translate('common:editors.toolTip.copyPaste', {limit: params.limit});
  return (
    getCustomToolTip(params, title) || <SearchFieldDefaultTooltip title={title} />
  );
};
export default React.memo(SearchFieldTooltip);
